import Column from '@app/components/layout/Column'
import { Breadcrumbs } from '@app/components/core/breadcrumbs/Breadcrumbs'
import { InitiativeViewPageLayout } from '@app/pages/layout/InitiativeViewPageLayout'
import { useEffect, useState } from 'react'
import { useClient, useAuthState } from '@based/sdk'
import { useTranslations } from '@i18n/useLanguage'
import { Initiative } from '@schema/initiative'
import { useLocation } from 'wouter'
import { pages } from '@app/util/links'
import { Title } from '@app/components/core/typography/Title'
import { EditButton } from '@app/components/core/button/EditButton'
import Row from '@app/components/layout/Row'
import { initiativeCreationSteps } from '../create/blocks/workflow'
import { Text } from '@app/components/core/typography/Text'
import { PreviewImage } from '@app/components/core/image/PreviewImage'
import useMediaQuery from '@app/util/useMediaQuery'

export const InitiativeAbout = ({ initiativeId }: { initiativeId: string }) => {
  const client = useClient()
  const authState = useAuthState()
  const isMobile = useMediaQuery()
  const [_, navigate] = useLocation()
  const translationsRoot = useTranslations().app
  const translations = translationsRoot.dashboard.initiative
  const [initiative, setInitiative] = useState<Partial<Initiative>>(null)
  useEffect(() => {
    client
      .query('get-initiative', {
        id: Number(initiativeId),
        userId: authState.userId,
      })
      .subscribe(
        (result) => {
          setInitiative(result)
        },
        () => navigate(pages.dashboardInitiatives),
      )
  }, [client, initiativeId, authState, setInitiative, navigate])

  if (!initiative) {
    return (
      <InitiativeViewPageLayout>
        <Column alignItems="stretch" gap="1.5rem">
          <Breadcrumbs
            levels={[
              {
                label: translations.common.myInitiatives,
                onClick: () => navigate(pages.dashboardInitiatives),
              },
              {
                label: translations.common.loading,
              },
            ]}
          />
        </Column>
      </InitiativeViewPageLayout>
    )
  }

  const title =
    initiative.title.trim() !== ''
      ? initiative.title
      : translations.common.unnamedInitiative

  return (
    <InitiativeViewPageLayout>
      <Column alignItems="stretch" gap="1.5rem" style={{ flexGrow: 0 }}>
        <Breadcrumbs
          levels={[
            {
              label: translations.common.myInitiatives,
              onClick: () => navigate(pages.dashboardInitiatives),
            },
            {
              label: title,
            },
          ]}
        />
        <Column gap="0.5rem" alignItems="stretch" style={{ flexGrow: 0 }}>
          <Row
            style={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Title.H3>{title}</Title.H3>
            {!isMobile && (
              <EditButton
                onClick={() =>
                  navigate(
                    pages.dashboardOnboardInitiative(
                      initiative.id.toString(),
                      initiativeCreationSteps.mainDetails,
                    ),
                  )
                }
              />
            )}
          </Row>
          <Text.Medium>{initiative.description}</Text.Medium>
        </Column>
        <Text.Medium>{initiative.vision}</Text.Medium>
      </Column>
      <Column gap="2rem" alignItems="stretch" style={{ flexGrow: 0 }}>
        <Row style={{ flexWrap: 'wrap' }}>
          {initiative.images.map((image, i) => (
            <PreviewImage key={i} src={image.src} />
          ))}
        </Row>
        <Column gap={'0.5rem'}>
          <Title.H5>{translations.view.developmentForm}</Title.H5>
          <Text.Medium>
            {initiative.developmentForm?.name ||
              translations.common.unknownDevelopmentForm}
          </Text.Medium>
        </Column>
        <Column gap={'0.5rem'}>
          <Title.H5>{translations.view.housingForms}</Title.H5>
          <Text.Medium>
            {initiative.housingForms.map((f) => f.title).join(', ')}
          </Text.Medium>
        </Column>
        <Column gap={'0.5rem'}>
          <Title.H5>{translations.view.targetGroups}</Title.H5>
          <Text.Medium>
            {initiative.targetAudiences.map((f) => f.name).join(', ')}
          </Text.Medium>
        </Column>
      </Column>
    </InitiativeViewPageLayout>
  )
}
